import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { DoodAuthenticationStep } from '@store/authentication/authentication.model';
import { AuthStoreDispatcher } from '@common/dispatchers/authentication.dispatcher';
import { AuthenticationScreenService } from '@core/services/authentication-screen/authentication-screen.service';
import { AuthenticationUserJourneyType } from '@shared/blocks/authentication-block/authentication-user-journey-type';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { CB2_PREVIEW_BLOC_KEY } from '@core/services/cb2-preview/cb2-preview-bloc-key.token';
import {
  AUTHENTICATION_MODAL_COMPONENT,
  resolveAuthenticationModalPreviewContent,
} from '@core/services/cb2-preview/authentication-modal-preview.util';
import { resolvePreviewScenarioForBloc } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

@Component({
  selector: 'app-authentication-block',
  templateUrl: './authentication-block.component.html',
  standalone: false,
})
export class AuthenticationBlockComponent implements OnInit, OnDestroy {
  @Input() userJourneyType = AuthenticationUserJourneyType.ASK_EMAIL_FIRST;
  @Input() legalCheckboxLabel?: string;
  @Input() optinCheckboxLabel?: string;

  @Input() firstStepHeading!: string;
  @Input() firstStepLoginButton!: string;
  @Input() firstStepSignupButton!: string;
  @Input() loginStepHeading!: string;
  @Input() signupStepHeading!: string;
  @Input() signupStepSubheading!: string;
  @Input() signupStepDisplayFirstname = true;
  @Input() signupStepFirstnameLabel!: string;
  @Input() signupStepFirstnameHelp!: string;
  @Input() signupStepDisplayLastname = true;
  @Input() signupStepLastnameLabel!: string;
  @Input() signupStepLastnameHelp!: string;
  @Input() signupStepEmailLabel!: string;
  @Input() signupStepEmailHelp!: string;
  @Input() signupStepDisplayPhone = true;
  @Input() signupStepPhoneLabel!: string;
  @Input() signupStepPhoneHelp!: string;
  @Input() signupStepPasswordLabel!: string;
  @Input() signupStepPasswordHelp!: string;
  @Input() signupFooter?: any;
  @Input() authenticationProviderGoogle = true;
  @Input() authenticationProviderFacebook = true;
  @Input() authenticationProviderApple = false;
  @Input() cgvUrl?: string;
  @Input() privacyPolicyUrl?: string;
  @Input() legalNoticeUrl?: string;

  steps = DoodAuthenticationStep;

  previewSubStep: 'HOME' | 'EMAIL_LOGIN' | null = null;
  previewUserJourneyType: AuthenticationUserJourneyType | null = null;

  authenticationStep$ = this.authSelector.selectStatusStep;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewBlocKey = inject(CB2_PREVIEW_BLOC_KEY, { optional: true });
  private readonly previewScenarios$ = toObservable(this.cb2PreviewState.previewScenarios);
  private readonly destroy$ = new Subject<boolean>();

  constructor(
    private authSelector: AuthStoreSelector,
    private authDispatcher: AuthStoreDispatcher,
    private readonly authenticationScreenService: AuthenticationScreenService,
  ) {
    if (!this.cb2PreviewState.isActive()) {
      this.authenticationScreenService.goToStep(DoodAuthenticationStep.login);
    }
  }

  ngOnInit(): void {
    if (!this.cb2PreviewState.isActive()) {
      return;
    }

    this.previewScenarios$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyPreviewScenario());

    this.applyPreviewScenario();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.authDispatcher.updateStatus({ step: null });
  }

  resolveUserJourneyType(): AuthenticationUserJourneyType {
    return this.previewUserJourneyType ?? this.userJourneyType;
  }

  private applyPreviewScenario(): void {
    const payload = resolvePreviewScenarioForBloc(
      this.cb2PreviewState.previewScenarios(),
      this.previewBlocKey,
      AUTHENTICATION_MODAL_COMPONENT,
    );
    const content = resolveAuthenticationModalPreviewContent(payload);

    if (!content) {
      this.previewSubStep = null;
      this.previewUserJourneyType = null;
      this.authenticationScreenService.goToStep(DoodAuthenticationStep.login);
      return;
    }

    this.authenticationScreenService.goToStep(
      content.authenticationStep === 'signup'
        ? DoodAuthenticationStep.signup
        : DoodAuthenticationStep.login,
    );
    this.previewSubStep = content.authenticationSubStep ?? null;
    this.previewUserJourneyType =
      content.userJourneyType === 'ASK_EMAIL_FIRST'
        ? AuthenticationUserJourneyType.ASK_EMAIL_FIRST
        : content.userJourneyType === 'ASK_LOGIN_OR_SIGNUP'
          ? AuthenticationUserJourneyType.ASK_LOGIN_OR_SIGNUP
          : null;
  }
}
